/**
 * Manipulación DIRECTA del diseño en modo editor: arrastrar la tarjeta de una
 * modal para posicionarla, redimensionarla desde bordes/esquinas, y arrastrar
 * un banner para alternar top/bottom. Vive en el bundle builder (veo-builder.js)
 * — el runtime de guías no carga nada de esto.
 *
 * Funciona como DECORATOR: se adjunta DESPUÉS del render sobre el shadow root
 * abierto del host de la guía (no toca los renderers). Durante el gesto aplica
 * las variables CSS localmente (feedback a 60fps, sin postMessage); al soltar
 * (`pointerup`) emite UN `onCommit(patch)` con los valores finales, que el
 * builder-mode postea al panel como `design-updated`. El panel actualiza su
 * estado y re-manda `preview` — que entra por el update in-place con las
 * MISMAS variables → no hay loop ni parpadeo.
 *
 * Geometría del modal (ver styles.ts): la card usa `left: posX%` +
 * `translate(-posX%)`, o sea `cardLeft = posX * (viewportW - cardW)` →
 * `posX = cardLeft / (viewportW - cardW)`. Mismo modelo que serializa el
 * dashboard (`style.posX/posY` en 0..1) y consume `applyDesignVars`.
 */
import type { GuideType } from '../guides/types';

/** Merge superficial sobre `step.style` del paso activo (lo aplica el panel). */
export interface DesignPatch {
  stepIndex: number;
  style: {
    posX?: number;
    posY?: number;
    width?: number;
    height?: number;
    position?: 'top' | 'bottom';
  };
}

export interface DesignManipulatorOptions {
  /** Host de la guía (de `PreviewHandle.host()`), con shadow root abierto. */
  host: HTMLElement;
  guideType: GuideType;
  stepIndex: number;
  /** Se emite UNA vez por gesto, al soltar. */
  onCommit: (patch: DesignPatch) => void;
  /** Fin de cualquier gesto (con o sin cambios). Para aplicar previews encolados. */
  onGestureEnd?: () => void;
}

export interface DesignManipulatorHandle {
  /** `true` mientras hay un gesto de drag/resize en curso. */
  isDragging(): boolean;
  /** Remueve handles, estilos y listeners. Idempotente. */
  detach(): void;
}

const MIN_W = 220;
const MAX_W = 720;
const MIN_H = 120;
const MAX_H = 900;
/** Zonas de agarre de los bordes (px). */
const EDGE = 8;

type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
const RESIZE_DIRS: ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

const MANIPULATOR_CSS = `
.veo-mnp-card {
  outline: 1.5px dashed rgba(255, 91, 53, 0.75);
  outline-offset: 2px;
}
.veo-mnp-card:hover { cursor: move; }
.veo-mnp-handle {
  position: absolute;
  z-index: 10;
  background: transparent;
}
.veo-mnp-handle::after {
  content: '';
  position: absolute;
  width: 8px; height: 8px;
  background: #fff;
  border: 1.5px solid rgba(255, 91, 53, 0.9);
  border-radius: 2px;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 100ms ease;
}
.veo-mnp-card:hover .veo-mnp-handle::after,
.veo-mnp-handle:hover::after { opacity: 1; }
.veo-mnp-n  { top: -${EDGE / 2}px; left: ${EDGE}px; right: ${EDGE}px; height: ${EDGE}px; cursor: ns-resize; }
.veo-mnp-s  { bottom: -${EDGE / 2}px; left: ${EDGE}px; right: ${EDGE}px; height: ${EDGE}px; cursor: ns-resize; }
.veo-mnp-e  { right: -${EDGE / 2}px; top: ${EDGE}px; bottom: ${EDGE}px; width: ${EDGE}px; cursor: ew-resize; }
.veo-mnp-w  { left: -${EDGE / 2}px; top: ${EDGE}px; bottom: ${EDGE}px; width: ${EDGE}px; cursor: ew-resize; }
.veo-mnp-ne { top: -${EDGE / 2}px; right: -${EDGE / 2}px; width: ${EDGE * 1.5}px; height: ${EDGE * 1.5}px; cursor: nesw-resize; }
.veo-mnp-nw { top: -${EDGE / 2}px; left: -${EDGE / 2}px; width: ${EDGE * 1.5}px; height: ${EDGE * 1.5}px; cursor: nwse-resize; }
.veo-mnp-se { bottom: -${EDGE / 2}px; right: -${EDGE / 2}px; width: ${EDGE * 1.5}px; height: ${EDGE * 1.5}px; cursor: nwse-resize; }
.veo-mnp-sw { bottom: -${EDGE / 2}px; left: -${EDGE / 2}px; width: ${EDGE * 1.5}px; height: ${EDGE * 1.5}px; cursor: nesw-resize; }
.veo-mnp-banner { outline: 1.5px dashed rgba(255, 91, 53, 0.75); outline-offset: -2px; cursor: grab; }
.veo-mnp-banner:active { cursor: grabbing; }
`;

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/** true si el mousedown cayó sobre algo interactivo (no iniciar drag ahí). */
function isInteractive(target: EventTarget | null): boolean {
  return Boolean(
    target instanceof Element && target.closest('button, a, input, textarea, select, label'),
  );
}

/**
 * Adjunta la manipulación directa al host de una guía montada. Devuelve el
 * handle, o `null` si el tipo de guía no la soporta o el DOM esperado no está.
 * Idempotente: si la card ya está decorada, no re-adjunta.
 */
export function attachDesignManipulator(
  opts: DesignManipulatorOptions,
): DesignManipulatorHandle | null {
  const shadow = opts.host.shadowRoot;
  if (!shadow) return null;

  const card = shadow.querySelector<HTMLElement>('.veo-modal-card');
  if (card) return attachModal(opts, shadow, card);

  const banner = shadow.querySelector<HTMLElement>('.veo-banner');
  // El banner embebido se posiciona con prepend/append en el contenedor: su
  // top/bottom se cambia desde el panel (remonta). Solo el fijo se arrastra.
  if (banner && !banner.classList.contains('veo-banner-embedded')) {
    return attachBanner(opts, shadow, banner);
  }

  return null;
}

// ── Modal: drag libre + resize por 8 handles ────────────────────────────────

function attachModal(
  opts: DesignManipulatorOptions,
  shadow: ShadowRoot,
  card: HTMLElement,
): DesignManipulatorHandle | null {
  if (card.dataset.veoManipulated === '1') return null;
  card.dataset.veoManipulated = '1';
  card.classList.add('veo-mnp-card');

  const doc = card.ownerDocument;
  const style = doc.createElement('style');
  style.textContent = MANIPULATOR_CSS;
  shadow.appendChild(style);

  const handles: HTMLElement[] = [];
  for (const dir of RESIZE_DIRS) {
    const h = doc.createElement('div');
    h.className = `veo-mnp-handle veo-mnp-${dir}`;
    h.dataset.veoDir = dir;
    card.appendChild(h);
    handles.push(h);
  }

  let dragging = false;
  let detached = false;

  /** Últimos valores aplicados durante el gesto (para el commit del up). */
  let patch: DesignPatch['style'] = {};

  const setVars = (v: { px?: number; py?: number; w?: number; h?: number }): void => {
    const host = opts.host;
    if (v.px !== undefined) host.style.setProperty('--veo-pos-x', `${v.px * 100}%`);
    if (v.py !== undefined) host.style.setProperty('--veo-pos-y', `${v.py * 100}%`);
    if (v.w !== undefined) host.style.setProperty('--veo-width', `${v.w}px`);
    if (v.h !== undefined) host.style.setProperty('--veo-min-h', `${v.h}px`);
  };

  /**
   * Un gesto genérico: guarda el rect inicial, traduce cada mousemove a
   * variables CSS (rAF-throttled) y al soltar emite el commit acumulado.
   */
  const startGesture = (
    e: MouseEvent,
    apply: (dx: number, dy: number, start: DOMRect) => DesignPatch['style'],
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    dragging = true;
    patch = {};
    const start = card.getBoundingClientRect();
    const sx = e.clientX;
    const sy = e.clientY;
    let raf = 0;
    let lastEv: MouseEvent | null = null;

    const flush = (): void => {
      raf = 0;
      if (!lastEv) return;
      const next = apply(lastEv.clientX - sx, lastEv.clientY - sy, start);
      patch = { ...patch, ...next };
      setVars({
        ...(next.posX !== undefined ? { px: next.posX } : {}),
        ...(next.posY !== undefined ? { py: next.posY } : {}),
        ...(next.width !== undefined ? { w: next.width } : {}),
        ...(next.height !== undefined ? { h: next.height } : {}),
      });
    };

    const move = (ev: MouseEvent): void => {
      lastEv = ev;
      // rAF-throttle del feedback; fallback síncrono si el entorno no tiene rAF.
      if (typeof window.requestAnimationFrame !== 'function') flush();
      else if (!raf) raf = window.requestAnimationFrame(flush);
    };
    const up = (): void => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      if (raf) window.cancelAnimationFrame(raf);
      flush();
      dragging = false;
      if (Object.keys(patch).length > 0 && !detached) {
        opts.onCommit({ stepIndex: opts.stepIndex, style: patch });
      }
      opts.onGestureEnd?.();
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  /** posX para que el borde izquierdo quede en `left` con ancho `w`. */
  const posXFor = (left: number, w: number): number => {
    const span = window.innerWidth - w;
    return span <= 0 ? 0.5 : round3(clamp(left / span, 0, 1));
  };
  const posYFor = (top: number, h: number): number => {
    const span = window.innerHeight - h;
    return span <= 0 ? 0.5 : round3(clamp(top / span, 0, 1));
  };

  const onCardDown = (e: MouseEvent): void => {
    if (isInteractive(e.target)) return;
    if (e.target instanceof Element && e.target.closest('.veo-mnp-handle')) return;
    startGesture(e, (dx, dy, start) => ({
      posX: posXFor(start.left + dx, start.width),
      posY: posYFor(start.top + dy, start.height),
    }));
  };

  const onHandleDown = (e: MouseEvent): void => {
    const dir = (e.currentTarget as HTMLElement).dataset.veoDir as ResizeDir;
    startGesture(e, (dx, dy, start) => {
      const out: DesignPatch['style'] = {};
      if (dir.includes('e')) out.width = Math.round(clamp(start.width + dx, MIN_W, MAX_W));
      if (dir.includes('w')) {
        out.width = Math.round(clamp(start.width - dx, MIN_W, MAX_W));
        // El borde derecho queda fijo: la card se re-ancla donde corresponde.
        out.posX = posXFor(start.right - out.width, out.width);
      }
      if (dir.includes('s')) out.height = Math.round(clamp(start.height + dy, MIN_H, MAX_H));
      if (dir.includes('n')) {
        out.height = Math.round(clamp(start.height - dy, MIN_H, MAX_H));
        out.posY = posYFor(start.bottom - out.height, out.height);
      }
      return out;
    });
  };

  card.addEventListener('mousedown', onCardDown);
  for (const h of handles) h.addEventListener('mousedown', onHandleDown);

  return {
    isDragging: () => dragging,
    detach: () => {
      if (detached) return;
      detached = true;
      card.removeEventListener('mousedown', onCardDown);
      for (const h of handles) h.remove();
      style.remove();
      card.classList.remove('veo-mnp-card');
      delete card.dataset.veoManipulated;
    },
  };
}

// ── Banner fijo: drag vertical con snap top/bottom ──────────────────────────

function attachBanner(
  opts: DesignManipulatorOptions,
  shadow: ShadowRoot,
  banner: HTMLElement,
): DesignManipulatorHandle | null {
  if (banner.dataset.veoManipulated === '1') return null;
  banner.dataset.veoManipulated = '1';
  banner.classList.add('veo-mnp-banner');

  const doc = banner.ownerDocument;
  const style = doc.createElement('style');
  style.textContent = MANIPULATOR_CSS;
  shadow.appendChild(style);

  let dragging = false;
  let detached = false;

  const positionOf = (): 'top' | 'bottom' =>
    banner.classList.contains('veo-banner-bottom') ? 'bottom' : 'top';

  const onDown = (e: MouseEvent): void => {
    if (isInteractive(e.target)) return;
    e.preventDefault();
    dragging = true;
    const startPos = positionOf();
    let current = startPos;

    const move = (ev: MouseEvent): void => {
      // Snap: la mitad del viewport decide top/bottom, con feedback en vivo.
      const next = ev.clientY < window.innerHeight / 2 ? 'top' : 'bottom';
      if (next !== current) {
        current = next;
        banner.classList.remove('veo-banner-top', 'veo-banner-bottom');
        banner.classList.add(`veo-banner-${next}`);
      }
    };
    const up = (): void => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      dragging = false;
      if (current !== startPos && !detached) {
        opts.onCommit({ stepIndex: opts.stepIndex, style: { position: current } });
      }
      opts.onGestureEnd?.();
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  banner.addEventListener('mousedown', onDown);

  return {
    isDragging: () => dragging,
    detach: () => {
      if (detached) return;
      detached = true;
      banner.removeEventListener('mousedown', onDown);
      style.remove();
      banner.classList.remove('veo-mnp-banner');
      delete banner.dataset.veoManipulated;
    },
  };
}
